import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  ExcelTableColDef,
  ECellValueDisplayMode,
} from 'components/ui/excel-table/types';
import { CatPricingSummaryRow, CatPricingSummaryTableProps } from './types';
import { Typography } from 'antd';

const { Title } = Typography;

const CatPricingSummaryTable: React.FC<CatPricingSummaryTableProps> = ({
  catPricingSummary,
  isCatPricingSummaryLoading,
}: CatPricingSummaryTableProps) => {
  const { t } = useTranslation();

  // In a real implementation, this data would likely come from an API call
  // For this example, we'll use mock data
  const rowData: CatPricingSummaryRow[] = useMemo(() => {
    return catPricingSummary.map((item) => ({
      layerNumber: item.layerNumber.toString(),
      layer: `Layer ${item.layerNumber}`,
      limit: item.limit,
      excess: item.excess,
      eqExpectedLossCost: item.totalExpectedLostCostByPeril.eq,
      flExpectedLossCost: item.totalExpectedLostCostByPeril.fl,
      tcExpectedLossCost: item.totalExpectedLostCostByPeril.tc,
      totalExpectedLossCost: item.totalExpectedLostCost,
      catRateAdequacy: (Number(item.catRateAdequacy) * 100).toFixed(2),
    }));
  }, [catPricingSummary]);

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'layer',
        headerName: t('catPricingSummary.headers.layer', 'Layer'),
        flex: 1,
        minWidth: 100,
        editable: false,
        resizable: true,
      },
      {
        field: 'limit',
        headerName: t('catPricingSummary.headers.limit', 'Limit (100%)'),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'excess',
        headerName: t('catPricingSummary.headers.excess', 'Excess (100%)'),
        flex: 1,
        minWidth: 120,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'eqExpectedLossCost',
        headerName: t(
          'catPricingSummary.headers.eqExpectedLossCost',
          'EQ Expected Loss Cost'
        ),
        flex: 1,
        minWidth: 160,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'flExpectedLossCost',
        headerName: t(
          'catPricingSummary.headers.flExpectedLossCost',
          'FL Expected Loss Cost'
        ),
        flex: 1,
        minWidth: 160,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'tcExpectedLossCost',
        headerName: t(
          'catPricingSummary.headers.tcExpectedLossCost',
          'TC Expected Loss Cost'
        ),
        flex: 1,
        minWidth: 160,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'totalExpectedLossCost',
        headerName: t(
          'catPricingSummary.headers.totalExpectedLossCost',
          'Total Expected Loss Cost'
        ),
        flex: 1,
        minWidth: 160,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return params.value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          });
        },
        resizable: true,
        precision: 0,
      },
      {
        field: 'catRateAdequacy',
        headerName: t(
          'catPricingSummary.headers.catRateAdequacy',
          'Cat Rate Adequacy'
        ),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          return `${(params.value * 100).toFixed(2)}%`;
        },
        resizable: true,
      },
    ],
    [t]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Title level={4} className="mb-6 ml-4">
        {t('catPricingSummary.catPricingSummary')}
      </Title>
      <ExcelTable
        rowKeyColName="id"
        baseTableHeight={380}
        columnDefs={columnDefs}
        data={rowData}
        tableClassName="rounded-t-lg"
        className="mb-4"
        pagination={false}
        includeIdRow={true}
        includeAddRow={false}
        includeDeleteCol={false}
        loading={isCatPricingSummaryLoading}
      />
    </div>
  );
};

export default CatPricingSummaryTable;
